import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../components/Loader.css';
import { GOLD, GOLD_LT, CREAM } from '../theme';
import { categoryImageUrl, sortedDressImages } from '../data/categoryImageUrl';
import { responsiveImage, CARD_WIDTHS } from '../data/responsiveImage';

// The homepage showed collections and categories but never a single garment,
// so the first individual piece was always two clicks away. Every couture
// house we looked at puts actual product on the front page. Take the leading
// piece from each category in turn so the row stays varied rather than eight
// bridal lehengas in a line.
// Three, not more: the grid collapses to one column on a phone, so each extra
// piece is another full card of vertical scroll before the visitor reaches the
// categories. "See everything" carries the rest.
const MAX_PIECES = 3;

function interleaveByCategory(categories, limit) {
  const queues = categories.map((cat) =>
    (cat.dresses || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((dress) => ({ dress, cat }))
  );
  const out = [];
  for (let round = 0; out.length < limit; round += 1) {
    let addedThisRound = false;
    for (const q of queues) {
      if (round < q.length && out.length < limit) {
        out.push(q[round]);
        addedThisRound = true;
      }
    }
    if (!addedThisRound) break; // every category exhausted
  }
  return out;
}

function PieceCard({ dress, cat }) {
  const [loaded, setLoaded] = useState(false);
  const cover = sortedDressImages(dress)[0];
  // Grid is auto-fill at 240px minimum, so a card is ~240-400px wide on
  // desktop and full-bleed-minus-gutter on a phone.
  const img = responsiveImage(
    categoryImageUrl(cover),
    CARD_WIDTHS,
    '(max-width: 640px) 92vw, (max-width: 1100px) 45vw, 23vw'
  );
  return (
    <Link
      to={`/categories/${cat.slug}/${dress.slug}`}
      className="ij-piece-card"
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 4', background: 'repeating-linear-gradient(135deg,#2a2016,#2a2016 14px,#231a11 14px,#231a11 28px)', border: '1px solid rgba(201,162,75,.28)', overflow: 'hidden' }}>
        {cover && !loaded && <div className="ij-loader-wrap"><span className="ij-loader" /></div>}
        {cover && (
          <img
            src={img.src}
            srcSet={img.srcSet}
            sizes={img.sizes}
            alt=""
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            className="ij-piece-img"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', opacity: loaded ? 1 : 0, transition: 'opacity .5s ease, transform .7s ease' }}
          />
        )}
      </div>
      <div style={{ marginTop: 14 }}>
        <h3 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontSize: 22, margin: '0 0 5px', color: CREAM }}>{dress.name}</h3>
        <span style={{ fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: GOLD_LT }}>{cat.name}</span>
      </div>
    </Link>
  );
}

export default function FeaturedPieces({ categories, loading }) {
  if (loading || !categories || categories.length === 0) return null;
  const picks = interleaveByCategory(categories, MAX_PIECES);
  if (picks.length === 0) return null;

  return (
    <section
      style={{ padding: 'clamp(64px,10vh,130px) clamp(20px,4vw,60px)', background: '#120d09', borderTop: '1px solid rgba(201,162,75,.2)' }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 44 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '.36em', textTransform: 'uppercase', color: GOLD, marginBottom: 14 }}>II — The Pieces</div>
          <h2 style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 500, fontStyle: 'italic', fontSize: 'clamp(38px,5.4vw,74px)', lineHeight: .98, margin: 0, color: CREAM }}>New in the atelier</h2>
        </div>
        <Link to="/categories" style={{ fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none', borderBottom: '1px solid rgba(226,190,118,.4)', paddingBottom: 6 }}>
          See everything →
        </Link>
      </div>

      {/* Three cards on a 1fr grid would each stretch to ~550px on a wide
          desktop, so the row is capped: cards settle around 380px and the
          section keeps the same measure as the rest of the page. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 'clamp(20px,2.4vw,34px)', maxWidth: picks.length * 400 }}>
        {picks.map(({ dress, cat }) => (
          <PieceCard key={`${cat.slug}/${dress.slug}`} dress={dress} cat={cat} />
        ))}
      </div>
    </section>
  );
}
